import React from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, RefreshCw, Loader2 } from 'lucide-react';
interface DraftColumnProps {
  sourceId: 'A' | 'B' | 'C';
  text?: string;
  status: 'loading' | 'error' | 'success' | 'idle';
  modelLabel?: string;
  errorMessage?: string;
  onSelectSentence: (sentence: string, sourceId: 'A' | 'B' | 'C') => void;
  onRetry?: () => void;
  allDraftsReady?: boolean;
  otherTexts?: string[];
}
export const DraftColumn: React.FC<DraftColumnProps> = ({
  sourceId,
  text,
  status,
  modelLabel,
  errorMessage,
  onSelectSentence,
  onRetry,
  allDraftsReady,
  otherTexts
}) => {
  const colors = {
    A: {
      header: 'bg-blue-100 text-blue-800',
      hover: 'hover:bg-blue-50',
      topBorder: 'border-t-blue-500',
      highlight: 'bg-blue-100/80 text-blue-900 rounded-sm px-0.5'
    },
    B: {
      header: 'bg-violet-100 text-violet-800',
      hover: 'hover:bg-violet-50',
      topBorder: 'border-t-violet-500',
      highlight: 'bg-violet-100/80 text-violet-900 rounded-sm px-0.5'
    },
    C: {
      header: 'bg-teal-100 text-teal-800',
      hover: 'hover:bg-teal-50',
      topBorder: 'border-t-teal-500',
      highlight: 'bg-teal-100/80 text-teal-900 rounded-sm px-0.5'
    }
  };
  // Split text into sentences for clickable selection.
  // The previous regex /[^.!?]+[.!?]+/g treated a leading numbered marker
  // like "1." as a complete sentence and DROPPED everything after it because
  // the trailing fragment had no terminator. That's why a heading translation
  // "1. Objet" rendered as just "1." in the UI. The new regex absorbs any
  // numbered prefix (1., 1.1., etc.) into the same chunk and falls back to
  // end-of-string so trailing text without a terminator is still captured.
  const sentences = text ? text.match(/(?:\d+\.)*\s*[^.!?]+(?:[.!?]+|$)/g) || [text] : [];
  // Pre-compute global words for diffing if all drafts are ready
  const globalWordsOther1 = allDraftsReady && otherTexts?.[0] ? otherTexts[0].trim().split(/\s+/) : [];
  const globalWordsOther2 = allDraftsReady && otherTexts?.[1] ? otherTexts[1].trim().split(/\s+/) : [];
  let globalWordIndex = 0;
  return <div className={`flex flex-col h-full min-h-0 border-r border-slate-200 last:border-r-0 bg-white border-t-4 ${colors[sourceId].topBorder}`} data-id="element-53">
      
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/50" data-id="element-54">
        <div className="flex items-center gap-2" data-id="element-55">
          <span className={`text-xs font-bold px-2.5 py-1 rounded-md ${colors[sourceId].header}`} data-id="element-56">
            
            Draft {sourceId}
          </span>
          {modelLabel && <span className="text-[11px] text-slate-400 font-medium" data-id="element-57">
              {modelLabel}
            </span>}
        </div>
        {status === 'loading' && <Loader2 className="w-3.5 h-3.5 text-slate-400 animate-spin" data-id="element-58" />}
      </div>

      <div className="flex-1 p-4 overflow-y-auto font-legal text-[16px] leading-[1.7] text-slate-800" data-id="element-59">
        {status === 'loading' && <div className="space-y-3" data-id="element-60">
            <div className="space-y-2 animate-pulse" data-id="element-61">
              <div className="h-4 bg-slate-200 rounded w-full" data-id="element-62" />
              <div className="h-4 bg-slate-200 rounded w-11/12" data-id="element-63" />
              <div className="h-4 bg-slate-200 rounded w-full" data-id="element-64" />
              <div className="h-4 bg-slate-200 rounded w-4/5" data-id="element-65" />
            </div>
            <p className="text-xs text-slate-400 font-sans mt-4 text-center" data-id="element-66">
              Translating with {modelLabel || `Model ${sourceId}`}...
            </p>
          </div>}

        {status === 'error' && <div className="flex flex-col items-center justify-center h-full text-center p-4" data-id="element-67">
            <AlertCircle className="w-8 h-8 text-red-400 mb-3" data-id="element-68" />
            <p className="text-sm text-slate-600 mb-1 font-sans font-medium" data-id="element-69">
              {modelLabel ? `${modelLabel} failed` : 'Failed to generate'}
            </p>
            {errorMessage && <p className="text-xs text-slate-400 font-sans mb-4 max-w-[200px] line-clamp-3" data-id="element-70">
                {errorMessage}
              </p>}
            <button onClick={onRetry} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50 font-sans transition-colors" data-id="element-71">
            
              <RefreshCw className="w-4 h-4" data-id="element-72" /> Retry
            </button>
          </div>}

        {status === 'success' && <div className="space-y-1" data-id="element-73">
            {sentences.map((sentence, idx) => {
          const tokens = sentence.split(/(\s+)/);
          return <motion.span key={idx} whileHover={{
            scale: 1.005
          }} onClick={() => onSelectSentence(sentence.trim(), sourceId)} className={`inline-block cursor-pointer rounded px-1 -mx-1 transition-colors ${colors[sourceId].hover}`} data-id="element-74">
                
                  {tokens.map((token, tokenIdx) => {
              if (!token.trim()) {
                return <span key={tokenIdx} data-id="element-75">{token}</span>;
              }
              let isDiff = false;
              if (allDraftsReady) {
                const wordOther1 = globalWordsOther1[globalWordIndex];
                const wordOther2 = globalWordsOther2[globalWordIndex];
                isDiff = token !== wordOther1 || token !== wordOther2;
              }
              globalWordIndex++;
              return <span key={tokenIdx} className={isDiff ? colors[sourceId].highlight : ''} data-id="element-76">
                      
                        {token}
                      </span>;
            })}
                </motion.span>;
        })}
          </div>}

        {status === 'idle' && <div className="flex items-center justify-center h-full text-slate-400 text-sm font-sans italic" data-id="element-77">
            Waiting to generate...
          </div>}
      </div>
    </div>;
};